import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  HStack,
  type HTMLChakraProps,
  TabsList as RawTabsList,
  TabsTrigger as RawTabsTrigger,
  type SegmentedControlOption,
  TabsRoot,
  TertiaryButton,
  TextButton,
  chakra,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { MultiSelect } from '~/components/common/MultiSelect'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import { formatDateValueJst } from '~/models/dateTime'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { CASE_SECTION_TABS } from '~/pages/WorkflowCases/models/filterOptions'
import { workflowCaseSectionLabelKey } from '~/pages/WorkflowCases/models/workflowCase'
import type { WorkflowCaseStatusSegment } from '~/pages/WorkflowCases/models/workflowCaseFilter'

// chakra v3 の `TabsList*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
// d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop が欠落する。
// v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。
// ランタイムは ecloud のまま。DS 側が d.ts を修正したら外してよい。
const TabsList = RawTabsList as React.FC<HTMLChakraProps<'div'>>
const TabsTrigger = RawTabsTrigger as React.FC<
  HTMLChakraProps<'button'> & { value: string }
>

export type FilterBarState = {
  section: WorkflowSection | null
  /**
   * ステータスフィルタ (spec AC-05、workflow-delete-transition design §4):
   * segment が基底集合を決め、showComplete/showDeleted が加算される
   * (`deriveWorkflowCaseStatuses` で repeated statuses に変換する)。
   */
  statusSegment: WorkflowCaseStatusSegment
  showComplete: boolean
  showDeleted: boolean
  /**
   * workflow-list-action-columns AC-07/AC-08、spec §機能詳細のフィルタ
   * (workflow.md §415): 「あなたが対応すべきもののみ」 (既定 OFF)。
   * server-side filter で、他のフィルタとは全て AND で合成される。
   * ステータス checkbox との連動は入れない (完了ケースは導出上自然に対象外
   * になるため)。
   */
  onlyViewerActionRequired: boolean
  /** workflow-fix-list AC-04/AC-06/AC-07: エリアの chip 式マルチ選択 (server-side filter) */
  areaCodes: AreaCode[]
  /** workflow-fix-list AC-05/AC-06/AC-07: BG の chip 式マルチ選択 (backend request field が無いため client-side filter) */
  bgCodes: string[]
  /**
   * AC-09: タグ label のチップ式マルチ選択 (OR 条件)。 backend の tag_labels フィルタは
   * 実装済みだが、 bgCode と同じ理由 (候補一覧を取得済み rows から導出するため
   * サーバー往復を追加しない) で client-side filter として扱う (index.tsx)。
   */
  tagLabels: string[]
  /**
   * AC-WFW-001 機能詳細: 対象日期間フィルタ (From ～ To の日付ピッカー + 当日/翌日
   * クイック絞り込みチップ)。 空文字 = 未指定 (フィルタなし)。
   */
  targetDateFrom: string
  targetDateTo: string
}

// spec AC-05: 既定 = セグメント「すべて」+ 両 checkbox OFF (= [NOT_STARTED, IN_PROGRESS] のみ表示)。
// workflow-fix-list: areaCodes/bgCodes は空配列 = フィルタなし (設計書 §5)。
export const DEFAULT_FILTER_STATE: FilterBarState = {
  section: null,
  statusSegment: 'all',
  showComplete: false,
  showDeleted: false,
  onlyViewerActionRequired: false,
  areaCodes: [],
  bgCodes: [],
  tagLabels: [],
  targetDateFrom: '',
  targetDateTo: '',
}

const ALL_TAB_VALUE = ''

type Props = {
  filter: FilterBarState
  onChange: (next: FilterBarState) => void
  /** spec AC-WFW-001: 対象コード候補 (BG + 発電の計画提出者、エリア連動なし) */
  bgOptions: ReadonlyArray<{ code: string; name: string }>
  /** AC-09: タグフィルタの候補元 (取得済み rows から導出した重複無し label 一覧) */
  tagOptions: string[]
}

export const FilterBar = ({
  filter,
  onChange,
  bgOptions,
  tagOptions,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const isFilterApplied =
    filter.section !== null ||
    filter.statusSegment !== 'all' ||
    filter.showComplete ||
    filter.showDeleted ||
    filter.onlyViewerActionRequired ||
    filter.areaCodes.length > 0 ||
    filter.bgCodes.length > 0 ||
    filter.tagLabels.length > 0 ||
    filter.targetDateFrom !== '' ||
    filter.targetDateTo !== ''

  // AC-WFW-001 機能詳細: 当日/翌日 のクイック絞り込みチップは From=To=該当日付をセット。
  // クリック時に new Date() を評価し、日付をまたいだ後も「当日 = 本日」になるよう固定しない。
  // 業務日付は JST 基準（mock-app Cases.razor の SlotTimeHelper.NowJst() 準拠）。
  const getTodayIso = (): string => formatDateValueJst()
  const getTomorrowIso = (): string =>
    formatDateValueJst(new Date(serverNow().getTime() + 24 * 60 * 60 * 1000))

  /**
   * 当日 / 翌日 は **set アクション**で、 押下状態 (selected) を持たない。
   *
   * DS に「選択解除できる弱い選択表現」 が無く、 chakra の solid/blue を当てると
   * DS のアクセント色とも 8080 とも違う青になる (デザイナー指摘 2026-08-12)。
   * スポット入札の同型 UI も 2026-06-10 に同じ理由で persistent active を撤去済み
   * (#5148 で 2026-08-12 に「現状維持」 として再確認)。
   *
   * 選択状態を持たないので**再押下での解除も無い** (解除は右のカレンダーの
   * 「選択をクリア」 か フィルタ解除で行う)。 押した結果は右のカレンダーの
   * 表示に出るので、 フィードバックはそこが担う。
   */
  const setTargetDateQuick = (range: 'today' | 'tomorrow'): void => {
    const target = range === 'today' ? getTodayIso() : getTomorrowIso()
    onChange({ ...filter, targetDateFrom: target, targetDateTo: target })
  }

  // ---------------------------------------------------------------------------
  // 対象日は **開始日 / 終了日の 2 入力**で持つ。
  //
  // 一度 ecloud `DateRangePicker` (受給期間と同じ部品) に寄せたが、 DS の期間カレンダーは
  // 「1 クリック目 = 開始日 / 2 クリック目 = 終了日」 しか作れず **「終了日のみ指定」 が
  // UI から到達不能**になっていた。 spec `workflow.md` AC-WFW-001 機能詳細 (P0) は
  // 「From のみ指定: From 以降を全て表示 / To のみ指定: To 以前を全て表示」 を要求しており、
  // backend (`workflow_case_repository.go` が From/To を独立に適用) / proto (`optional`) /
  // request 組み立ても片側指定に対応済みで、 **UI だけが作れない**状態だった。
  //
  // デザイナー判断 (2026-08-12) により 2 入力へ戻す。 「受給期間と同じ部品にする」 という
  // 先の指示より **片側指定できること (spec P0)** を優先する。
  // ---------------------------------------------------------------------------

  // AC-05: セグメント (すべて/未着手/進行中) の選択肢。
  // design (design/demand/cases.html の `.ecl-segmented`) が SegmentedControl
  // (帯の中の白セグメント) で描いているため DS `SegmentedControl` を使う。
  // [規約差異] 択一グループは ToggleButton が既定だが、 ToggleButton (solid/blue + outline) は design と別部品の見た目になり、
  // 非選択の枠 3px / 文字 12px も DS トークン外の chakra 既定値で描画されていた
  // (2026-07-30 実測)。 design を UI の正本とする `design/CLAUDE.md` に従い design 側へ寄せる。
  // フィルタ用途での SegmentedControl 採用可否は issue #3381 (Des確認) の論点でもあるため、
  // 規約と #3381 の確定内容に応じて再度寄せ先を見直す。
  const statusSegmentOptions: Array<
    SegmentedControlOption<FilterBarState['statusSegment']>
  > = [
    { value: 'all', label: t('page:workflowCases.filter.all') },
    {
      value: 'notStarted',
      label: t('page:workflowCases.filter.statusSegmentNotStarted'),
    },
    {
      value: 'inProgress',
      label: t('page:workflowCases.filter.statusSegmentInProgress'),
    },
  ]

  const sectionTabItems: ReadonlyArray<{
    key: string
    label: React.ReactNode
  }> = [
    { key: ALL_TAB_VALUE, label: t('page:workflowCases.filter.all') },
    ...CASE_SECTION_TABS.map((section) => ({
      key: String(section),
      label: t(workflowCaseSectionLabelKey(section) ?? ''),
    })),
  ]

  return (
    <>
      {/* 計画断面フィルタ: design (design/demand/cases.html:156-161) の `.ecl-tabs` を
          再現する raw slot tabs に統一 (旧 SegmentedControl から差し替え)。design は
          タブ行をフィルタ群と独立させた行にしているため、ここでも
          FilterToolbar (ステータス/エリア/BG) とは別行で描画する。 */}
      <TabsRoot
        onValueChange={(details: { value: string }) => {
          const key = details.value
          onChange({
            ...filter,
            section:
              key === ALL_TAB_VALUE ? null : (Number(key) as WorkflowSection),
          })
        }}
        value={filter.section === null ? ALL_TAB_VALUE : String(filter.section)}
      >
        {/* paddingBottom 1px は選択タブの下線を削らせないための実寸補正。
            DS の選択タブは `border-bottom 2px` + `marginBottom -1px` で list の
            境界線に重ねる作りなので、 1px が list の外へはみ出す。 overflowY: hidden
            (= 横スクロール時の縦スクロールバー抑止) と併せると、そのはみ出した 1px が
            padding box で clip されて下線が 1px に見える (2026-07-30 dev 実測)。
            padding で 1px 分の逃げを作ると 2px 全体が描画される。 */}
        <TabsList
          overflowX="auto"
          overflowY="hidden"
          paddingBottom="1px"
          aria-label={t('page:workflowCases.filter.section')}
        >
          {sectionTabItems.map((item) => (
            <TabsTrigger
              key={item.key}
              fontSize={tabTriggerFontSize.token}
              value={item.key}
            >
              <span>{item.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      <FilterToolbar
        labelVariant="formLabel"
        resetAction={
          <TextButton
            disabled={!isFilterApplied}
            onClick={() => {
              onChange(DEFAULT_FILTER_STATE)
            }}
            size="sm"
          >
            {t('page:workflowCases.filter.reset')}
          </TextButton>
        }
      >
        {/* AC-WFW-001 機能詳細: 対象日期間フィルタ (期間ピッカー + 当日/翌日 クイック
            絞り込みチップ)。 クイックチップは From=To=該当日付をセットし、 再クリックで
            解除する (mock-app Cases.razor L26-37 準拠)。

            配置: フィルタ行の先頭 (= ステータスの左)。 デザイナー指示 (2026-08-12)
            「対象日をステータスの左へ」 が唯一の根拠 — 見た目の正本である 8080
            プロトタイプ (design/demand/cases.html) には対象日フィルタ自体が存在せず
            (プロトの並び: ステータス → 完了を表示 → 削除を表示 → タグ → エリア → BG →
            あなたの要対応のみ)、 参照できる並び順が無いため。 プロトに対象日が
            追加されたら、 そちらを正本として並びを再確認する。 */}
        <FilterSection label={t('page:workflowCases.filter.targetDate')}>
          <HStack alignItems="center" gap={2}>
            <ButtonTooltip
              content={t('page:workflowCases.filter.targetDateQuickOnly', {
                date: getTodayIso(),
              })}
            >
              <TertiaryButton
                aria-label={t('page:workflowCases.filter.targetDateQuickToday')}
                onClick={() => {
                  setTargetDateQuick('today')
                }}
                size="md"
                title={t('page:workflowCases.filter.targetDateQuickOnly', {
                  date: getTodayIso(),
                })}
              >
                {t('page:workflowCases.filter.targetDateQuickToday')}
              </TertiaryButton>
            </ButtonTooltip>
            <ButtonTooltip
              content={t('page:workflowCases.filter.targetDateQuickOnly', {
                date: getTomorrowIso(),
              })}
            >
              <TertiaryButton
                aria-label={t(
                  'page:workflowCases.filter.targetDateQuickTomorrow',
                )}
                onClick={() => {
                  setTargetDateQuick('tomorrow')
                }}
                size="md"
                title={t('page:workflowCases.filter.targetDateQuickOnly', {
                  date: getTomorrowIso(),
                })}
              >
                {t('page:workflowCases.filter.targetDateQuickTomorrow')}
              </TertiaryButton>
            </ButtonTooltip>
            {/* 開始日 / 終了日を独立に指定できる 2 入力 (片側だけの指定も可)。
                spec AC-WFW-001 機能詳細の「From のみ / To のみ」 を満たすため。 

                幅は共通の日付欄の幅 (`COMPACT_DATE_FIELD_WIDTH`、 曜日付きの値と
                × ボタンが収まる幅)。 元の 176px は placeholder が 150px で見切れた
                (2026-08-12 デザイナー指摘) ための値だった。 */}
            <Box width={COMPACT_DATE_FIELD_WIDTH}>
              <chakra.label
                css={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                  clipPath: 'inset(50%)',
                  whiteSpace: 'nowrap',
                }}
                htmlFor="workflow-cases-target-date-from"
              >
                {t('page:workflowCases.filter.targetDateFrom')}
              </chakra.label>
              <CompactCivilDateField
                hasClear
                id="workflow-cases-target-date-from"
                onChange={(d) => {
                  onChange({
                    ...filter,
                    targetDateFrom: d ? civilDateToInputValue(d) : '',
                  })
                }}
                value={parseDateInputValue(filter.targetDateFrom)}
              />
            </Box>
            <span>
              {t('page:workflowCases.filter.targetDateRangeSeparator')}
            </span>
            <Box width={COMPACT_DATE_FIELD_WIDTH}>
              <chakra.label
                css={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                  clipPath: 'inset(50%)',
                  whiteSpace: 'nowrap',
                }}
                htmlFor="workflow-cases-target-date-to"
              >
                {t('page:workflowCases.filter.targetDateTo')}
              </chakra.label>
              <CompactCivilDateField
                hasClear
                id="workflow-cases-target-date-to"
                onChange={(d) => {
                  onChange({
                    ...filter,
                    targetDateTo: d ? civilDateToInputValue(d) : '',
                  })
                }}
                value={parseDateInputValue(filter.targetDateTo)}
              />
            </Box>
          </HStack>
        </FilterSection>

        {/* AC-05 (workflow-delete-transition design §4): セグメント (すべて/未着手/
            進行中) が基底集合を決め、下の checkbox 2 個 (完了を表示/削除を表示) が
            セグメントと独立に加算される。既定 = セグメント「すべて」+ 両 OFF。 */}
        <FilterSection label={t('page:workflowCases.filter.status')}>
          {/* DS `SegmentedControl` そのままでは radio がキーボードからも a11y tree からも
              失われる (= `display: none`) ため、 補正込みの共通ラッパを使う。
              補正の中身と「外してはいけない理由」は AccessibleSegmentedControl の JSDoc 参照。 */}
          <AccessibleSegmentedControl
            aria-label={t('page:workflowCases.filter.status')}
            name="workflow-cases-status"
            onSelect={(value) => {
              onChange({ ...filter, statusSegment: value })
            }}
            options={statusSegmentOptions}
            selected={filter.statusSegment}
          />
        </FilterSection>

        {/* checkbox の文言は FilterSection のラベル (太字・左) ではなく Checkbox 自身に
            持たせる。 design (design/demand/cases.html の `.ecl-checkbox`) は「箱が左 +
            細字の文言」で、`design/CLAUDE.md` も「checkbox の文言は項目ラベルでなく値
            なので太字にしない」と定めている (旧実装は FilterSection ラベル + 文言なし
            Checkbox のため、 箱が右・文言が太字になっていた。 2026-07-30 実測)。 */}
        <Checkbox
          checked={filter.showComplete}
          onCheckedChange={(details: {
            checked: boolean | 'indeterminate'
          }) => {
            onChange({ ...filter, showComplete: details.checked === true })
          }}
        >
          {t('page:workflowCases.filter.showComplete')}
        </Checkbox>

        <Checkbox
          checked={filter.showDeleted}
          onCheckedChange={(details: {
            checked: boolean | 'indeterminate'
          }) => {
            onChange({ ...filter, showDeleted: details.checked === true })
          }}
        >
          {t('page:workflowCases.filter.showDeleted')}
        </Checkbox>

        {/* workflow-fix-list AC-04/AC-06/AC-07: エリアの chip 式マルチ選択
            (server-side filter、既存 MultiSelect をタグフィルタと同様に使う)。 */}
        <FilterSection label={t('page:workflowCases.filter.area')}>
          <Box flexShrink={0} maxWidth="100%">
            <MultiSelect
              aria-label={t('page:workflowCases.filter.area')}
              onChange={(next) => {
                onChange({
                  ...filter,
                  areaCodes: next.map((v) => Number(v) as AreaCode),
                })
              }}
              options={AREA_CODE_OPTIONS.map((code) => ({
                value: String(code),
                label: convertAreaLabel(code),
              }))}
              placeholder={t('page:workflowCases.filter.allAreas')}
              selected={filter.areaCodes.map(String)}
              sizing="fit"
            />
          </Box>
        </FilterSection>

        {/* workflow-fix-list AC-05/AC-06/AC-07: BG の chip 式マルチ選択
            (backend request field が無いため index.tsx で client-side filter)。 */}
        <FilterSection label={t('page:workflowCases.filter.bg')}>
          <Box flexShrink={0} maxWidth="100%">
            <MultiSelect
              aria-label={t('page:workflowCases.filter.bg')}
              onChange={(next) => {
                onChange({ ...filter, bgCodes: next })
              }}
              options={bgOptions.map((bg) => ({
                value: bg.code,
                label: `${bg.code} - ${bg.name}`,
              }))}
              placeholder={t('page:workflowCases.filter.allBgs')}
              selected={filter.bgCodes}
              sizing="fit"
            />
          </Box>
        </FilterSection>

        {/* AC-09: タグのチップ式マルチ選択。 design (2026-07-14 決定): フィルタ側の
            色付けは一貫性が無く視認補助にならないため、 プレーンテキストの chip
            (WorkflowCaseTagEditor/List の色付き chip とは意図的に別表現)。 */}
        <FilterSection label={t('page:workflowCases.filter.tags')}>
          {/* **[design 差異]** タグも エリア / BG と同じ `sizing="fit"` にする。
              design `demand/cases.html` の scoped override は `#cases-area-ms` /
              `#cases-bg-ms` だけを対象にしていて**タグは含まない**が、
              「タグも同じ挙動にすべき」 というデザイナー判断 (2026-08-13)。
              タグは最大 20 文字の自由文字列なので、 エリア / BG より横に伸びやすい
              (行に収まらなくなったら親の `flexWrap` でフィルタ項目ごと改行する)。
              design 側を追従させるかは別途確認。 */}
          <Box flexShrink={0} maxWidth="100%">
            <MultiSelect
              aria-label={t('page:workflowCases.filter.tags')}
              emptyText={t('page:workflowCases.filter.tagsEmpty')}
              onChange={(next) => {
                onChange({ ...filter, tagLabels: next })
              }}
              options={tagOptions.map((label) => ({
                value: label,
                label: localizeBuiltinWorkflowText(label, t),
              }))}
              placeholder={t('page:workflowCases.filter.allTags')}
              selected={filter.tagLabels}
              sizing="fit"
            />
          </Box>
        </FilterSection>

        {/* workflow-list-action-columns AC-07/AC-08、spec §機能詳細の
            フィルタ (workflow.md §415): 「あなたが対応すべきもののみ」。
            server-side filter (= backend が viewer から要承認/要対応を
            導出して絞る) で、既定 OFF。viewer は BFF が eauth から注入する
            ため request には載せない。 */}
        <Checkbox
          checked={filter.onlyViewerActionRequired}
          onCheckedChange={(details: {
            checked: boolean | 'indeterminate'
          }) => {
            onChange({
              ...filter,
              onlyViewerActionRequired: details.checked === true,
            })
          }}
        >
          {t('page:workflowCases.filter.onlyViewerActionRequired')}
        </Checkbox>
      </FilterToolbar>
    </>
  )
}
